/** Opens a formatted PDF-ready report in a new tab and triggers print. */
export function printFieldReport(result) {
  if (!result) return;
  const r = result;
  const crop = r.crop_type || r.crop || r.inputs?.crop || "";
  const date = new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" });

  const row = (label, value, note="") =>
    `<tr><td>${label}</td><td><strong>${value ?? "—"}</strong></td><td style="color:#666">${note}</td></tr>`;

  const section = (title, rows) =>
    `<h3>${title}</h3><table><thead><tr><th>Parameter</th><th>Value</th><th>Note</th></tr></thead><tbody>${rows}</tbody></table>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>OpenFarm Field Report — ${date}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #222; margin: 0; padding: 24px; }
  h1 { font-size: 20px; color: #1a2e1a; margin: 0 0 2px; }
  .subtitle { font-size: 12px; color: #666; margin-bottom: 20px; }
  h3 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; color: #1a2e1a;
       border-bottom: 1px solid #ccc; padding-bottom: 4px; margin: 20px 0 6px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; color: #999;
       padding: 4px 6px; border-bottom: 1px solid #ddd; }
  td { padding: 5px 6px; border-bottom: 0.5px solid #eee; vertical-align: top; }
  tr:last-child td { border-bottom: none; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px;
           font-weight: 700; color: #fff; }
  .actions { margin-top: 6px; }
  .action-item { padding: 4px 0; border-bottom: 0.5px solid #f0f0f0; font-size: 12px; }
  .footer { margin-top: 32px; font-size: 10px; color: #aaa; border-top: 1px solid #eee; padding-top: 8px; }
  @page { margin: 15mm; size: A4 portrait; }
  @media print { body { padding: 0; } }
</style></head><body>

<h1>OpenFarm Field Report</h1>
<div class="subtitle">Crop: <strong>${crop}</strong> &nbsp;|&nbsp; Generated: ${date}</div>

${section("Field Overview", [
  row("Health Status", `<span class="badge" style="background:${r.health_status==="Healthy"?"#639922":r.health_status==="Moderate"?"#EF9F27":"#E24B4A"}">${r.health_status}</span>`),
  row("NDVI (mean)", r.ndvi, "0–1, higher = healthier vegetation"),
  row("NDVI Change", r.ndvi_change != null ? (r.ndvi_change >= 0 ? "+" : "") + r.ndvi_change : "—", "vs. previous period"),
  row("NDMI", r.ndmi ?? "—", "moisture stress indicator"),
  row("Nitrogen proxy (NDRE)", r.nitrogen ?? "—", "leaf nitrogen estimate"),
].join(""))}

${r.indices ? section("Spectral Indices", Object.entries(r.indices).map(([k,v]) =>
  row(k, v.mean != null ? v.mean : "—", v.p10 != null ? `p10: ${v.p10}  p90: ${v.p90}` : "")
).join("")) : ""}

${r.weather && !r.weather.error ? section("Weather (7-day)", [
  row("Avg Temperature", `${r.weather.avg_temp_7d} °C`),
  row("Max Temperature", `${r.weather.max_temp_7d} °C`),
  row("Total Rainfall", `${r.weather.total_precip_7d_mm} mm`),
  row("Avg Humidity", `${r.weather.avg_rh_7d_pct} %`),
  row("Consecutive Dry Days", `${r.weather.consecutive_dry_days} days`),
  row("ET₀ (7-day)", `${r.weather.et0_7d_mm} mm`),
  r.weather.precip_anomaly_pct != null ? row("Rainfall Anomaly", `${r.weather.precip_anomaly_pct > 0 ? "+" : ""}${r.weather.precip_anomaly_pct}%`, `normal: ${r.weather.weekly_normal_mm} mm/wk`) : "",
].join("")) : ""}

${r.soil && !r.soil.error ? section("Soil Profile", [
  row("Clay", r.soil.clay_pct != null ? `${r.soil.clay_pct}%` : "—"),
  row("Sand", r.soil.sand_pct != null ? `${r.soil.sand_pct}%` : "—"),
  row("Silt", r.soil.silt_pct != null ? `${r.soil.silt_pct}%` : "—"),
  row("pH", r.soil.ph ?? "—", r.soil.ph < 5.5 ? "Acidic" : r.soil.ph > 7.5 ? "Alkaline" : "Neutral"),
  row("Organic Carbon", r.soil.soc_g_kg != null ? `${r.soil.soc_g_kg} g/kg` : "—"),
  row("Bulk Density", r.soil.bulk_density != null ? `${r.soil.bulk_density} g/cm³` : "—"),
  row("Texture Class", r.soil.texture_class ?? "—"),
  row("Drainage", r.soil.drainage ?? "—"),
  row("Source", r.soil.soil_source ?? "—", r.soil.soil_resolution ?? ""),
].join("")) : ""}

${r.climate ? section("Climate", [
  row("Köppen Zone", r.climate.koppen ?? "—"),
  row("Climate Class", r.climate.climate_class ?? "—"),
  row("Annual Rainfall", r.climate.annual_precip_mm != null ? `${r.climate.annual_precip_mm} mm` : "—"),
  row("Mean Annual Temp", r.climate.mean_temp_c != null ? `${r.climate.mean_temp_c} °C` : "—"),
  r.climate.suitability_label ? row("Crop Suitability", r.climate.suitability_label) : "",
].join("")) : ""}

${r.ecostress && r.ecostress.available ? section("Thermal Analysis", [
  r.ecostress.lst_c != null ? row("Land Surface Temp", `${r.ecostress.lst_c} °C`) : "",
  r.ecostress.et_mm_day != null ? row("Evapotranspiration", `${r.ecostress.et_mm_day} mm/day`) : "",
  r.ecostress.heat_stress_flag != null ? row("Heat Stress Flag", r.ecostress.heat_stress_flag ? "Yes" : "No") : "",
].join("")) : ""}

${r.risks && r.risks.length > 0 ? `<h3>Alerts &amp; Issues (${r.risks.length})</h3>
<table><thead><tr><th>Issue</th><th>Severity</th><th>Recommendation</th></tr></thead><tbody>
${r.risks.map(a => `<tr><td>${a.name}</td><td><span class="badge" style="background:${a.level==="Severe"||a.level==="High"?"#E24B4A":a.level==="Medium"?"#EF9F27":"#639922"}">${a.level}</span></td><td style="color:#555;font-size:11px">${a.recommendation||""}</td></tr>`).join("")}
</tbody></table>` : ""}

${r.actions && r.actions.length > 0 ? `<h3>Recommended Actions</h3>
<div class="actions">${r.actions.map((a,i) => `<div class="action-item">${i+1}. ${typeof a === "string" ? a : a.action}${a.urgency ? ` <span style="color:#888;font-size:10px">(${a.urgency})</span>` : ""}</div>`).join("")}</div>` : ""}

${r.future_crop_recommendations && r.future_crop_recommendations.length > 0 ? `<h3>Future Crop Recommendations</h3>
<table><thead><tr><th>Crop</th><th>Score</th><th>Reasons</th></tr></thead><tbody>
${r.future_crop_recommendations.slice(0,8).map(c => `<tr><td>${c.crop||"—"}</td><td>${c.score ?? "—"}</td><td style="font-size:11px;color:#555">${(c.match_reasons||[]).join("; ")||"—"}</td></tr>`).join("")}
</tbody></table>` : ""}

${r.data_sources ? `<h3>Data Sources</h3><table><tbody>
${[["Satellite",r.data_sources.satellite],["Weather",r.data_sources.weather],["Soil",r.data_sources.soil],["Climate",r.data_sources.climate],["Thermal",r.data_sources.thermal]].filter(([,v])=>v).map(([l,v])=>`<tr><td>${l}</td><td style="color:#555">${v}</td></tr>`).join("")}
</tbody></table>` : ""}

<div class="footer">Generated by OpenFarm &nbsp;|&nbsp; ${date}</div>
</body></html>`;

  const w = window.open("", "_blank", "width=800,height=900");
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}
